"use client"

import { FormikProvider, type FormikErrors, useFormik } from "formik"
import { useState } from "react"

import { FormInput } from "@/components/form-inputs"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { DialogFooter } from "@/components/ui/dialog"
import { useSendMetaAdsTestLeadMutation } from "@/hooks/mutations/meta-ads"
import { normalizeMetaTestPhoneNumber } from "@/lib/meta-ads/phone"
import type { MetaAdsIntegrationView } from "@/lib/meta-ads/types"
import { cn } from "@/lib/utils"

type MetaAdsTestValues = {
  phoneNumber: string
}

export function MetaAdsTestForm({
  onCancel,
  onSuccess,
  cancelLabel = "Cancel",
  footerClassName,
}: {
  onCancel: () => void
  onSuccess: (integration: MetaAdsIntegrationView) => void
  cancelLabel?: string
  footerClassName?: string
}) {
  const mutation = useSendMetaAdsTestLeadMutation()
  const [error, setError] = useState<string | null>(null)
  const formik = useFormik<MetaAdsTestValues>({
    initialValues: { phoneNumber: "" },
    validate: validateTestLead,
    onSubmit: async (values, helpers) => {
      setError(null)
      try {
        const integration = await mutation.mutateAsync({
          phoneNumber: normalizeMetaTestPhoneNumber(values.phoneNumber)
            ?? values.phoneNumber.trim(),
          setErrors: errors => helpers.setErrors(errors as FormikErrors<MetaAdsTestValues>),
        })
        onSuccess(integration)
      } catch (submitError: unknown) {
        setError(getErrorMessage(submitError))
      }
    },
  })

  return (
    <FormikProvider value={formik}>
      <form className="space-y-4" onSubmit={formik.handleSubmit}>
        <Alert>
          <AlertTitle>Use your own phone number</AlertTitle>
          <AlertDescription>
            This is a real end-to-end test. It creates a real callback task and may place
            a real outbound call from your configured caller number.
          </AlertDescription>
        </Alert>
        {error ? (
          <Alert variant="destructive" role="alert">
            <AlertTitle>The test lead could not be sent</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <FormInput
          name="phoneNumber"
          type="tel"
          label="Phone number to call"
          placeholder="+351…"
          description="Include the country code, starting with +. Spaces are okay."
        />
        <DialogFooter className={cn(footerClassName)}>
          <Button type="button" variant="outline" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? "Sending test…" : "Send test lead"}
          </Button>
        </DialogFooter>
      </form>
    </FormikProvider>
  )
}

function validateTestLead(values: MetaAdsTestValues): FormikErrors<MetaAdsTestValues> {
  if (!normalizeMetaTestPhoneNumber(values.phoneNumber)) {
    return { phoneNumber: "Enter a complete phone number with country code, such as +351…" }
  }
  return {}
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return "The Meta test lead could not be sent. Try again."
}
